// services/pushNotificationService.js
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDocs, 
  query, 
  where,
  Timestamp,
  setDoc,
  getDoc
} from 'firebase/firestore';
import { db } from './firebaseConfig';

/**
 * Request browser notification permission
 * @returns {Promise<string>} Permission status: 'granted', 'denied', or 'default'
 */
export const requestNotificationPermission = async () => {
  try {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return 'unsupported';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission;
    }

    return Notification.permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'error';
  }
};

/**
 * Check if notifications are supported and enabled
 * @returns {boolean}
 */
export const areNotificationsEnabled = () => {
  return 'Notification' in window && Notification.permission === 'granted';
};

/**
 * Save user's notification preference and device token to Firestore
 * @param {string} userId - User ID
 * @param {string} role - User role (admin, doctor, finance, hospital, etc.)
 * @param {boolean} enabled - Whether notifications are enabled
 * @returns {Promise}
 */
export const saveNotificationPreference = async (userId, role, enabled) => {
  try {
    const prefDoc = doc(db, 'notificationPreferences', userId);
    await setDoc(prefDoc, {
      userId,
      role,
      enabled,
      permission: Notification.permission,
      updatedAt: Timestamp.now()
    }, { merge: true });

    return { success: true };
  } catch (error) {
    console.error('Error saving notification preference:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get notification preference for a user
 * @param {string} userId - User ID
 * @returns {Promise}
 */
export const getNotificationPreference = async (userId) => {
  try {
    const prefDoc = doc(db, 'notificationPreferences', userId);
    const snapshot = await getDoc(prefDoc);
    
    if (snapshot.exists()) {
      return { success: true, preference: snapshot.data() };
    }
    
    return { success: true, preference: null };
  } catch (error) {
    console.error('Error getting notification preference:', error);
    return { success: false, error: error.message, preference: null };
  }
};

/**
 * Show browser notification
 * @param {Object} options - Notification options
 * @param {string} options.title - Notification title
 * @param {string} options.body - Notification body
 * @param {string} options.icon - Icon URL (optional)
 * @param {string} options.tag - Notification tag for grouping (optional)
 * @param {Object} options.data - Custom data to pass (optional)
 * @returns {Notification|null}
 */
export const showBrowserNotification = (options) => {
  try {
    if (!areNotificationsEnabled()) {
      console.warn('Notifications not enabled');
      return null;
    }

    const {
      title,
      body,
      icon = '/samhealth.png', // SamHealth logo
      tag,
      data = {}
    } = options;

    const notification = new Notification(title, {
      body,
      icon,
      tag,
      data,
      badge: '/samhealth.png', // SamHealth badge icon
      requireInteraction: false, // Auto-dismiss after a few seconds
      silent: false
    });

    // Handle notification click
    notification.onclick = (event) => {
      event.preventDefault();
      
      // Close the notification
      notification.close();
      
      // Focus the window
      window.focus();
      
      // Navigate to the URL if provided
      if (data.actionUrl) {
        window.location.href = data.actionUrl;
      }
    };

    return notification;
  } catch (error) {
    console.error('Error showing browser notification:', error);
    return null;
  }
};

/**
 * Get users by role for targeted notifications
 * @param {string} role - Role to filter by
 * @returns {Promise<Array>} Array of user IDs with notification preferences enabled
 */
const getUsersByRole = async (role) => {
  try {
    const q = query(
      collection(db, 'notificationPreferences'),
      where('role', '==', role),
      where('enabled', '==', true),
      where('permission', '==', 'granted')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data().userId);
  } catch (error) {
    console.error('Error getting users by role:', error);
    return [];
  }
};

/**
 * Generate action URL based on role and application ID
 * @param {string} role - User role
 * @param {string} applicationId - Application ID
 * @param {string} disputeId - Dispute ID (optional)
 * @returns {string} URL to navigate to
 */
const generateActionUrl = (role, applicationId, disputeId = null) => {
  const baseUrl = window.location.origin;
  
  const roleUrls = {
    admin: `/admin/applications/${applicationId}`,
    doctor: `/doctor/review/${applicationId}`,
    finance: `/financial/applications/${applicationId}`,
    hospital: `/hospital/applications/${applicationId}`,
    policy: `/policy/applications/${applicationId}`
  };
  
  let url = roleUrls[role] || `/applications/${applicationId}`;
  
  // If dispute ID is provided, navigate to dispute/query section
  if (disputeId) {
    url += `?tab=disputes`;
  }
  
  return baseUrl + url;
};

/**
 * Send push notification to specific users
 * @param {Object} params - Notification parameters
 * @param {Array<string>} params.userIds - Array of user IDs to send to
 * @param {string} params.title - Notification title
 * @param {string} params.message - Notification message
 * @param {string} params.applicationId - Application ID (optional)
 * @param {string} params.disputeId - Dispute ID (optional)
 * @param {string} params.role - User role for URL generation
 * @returns {Promise}
 */
export const sendPushNotificationToUsers = async (params) => {
  try {
    const {
      userIds,
      title,
      message,
      applicationId,
      disputeId,
      role
    } = params;

    // Only send to current user if they are in the list and have notifications enabled
    const currentUserId = getCurrentUserId(); // You'll need to implement this
    
    if (!userIds.includes(currentUserId)) {
      return { success: true, sent: false, reason: 'Not applicable to current user' };
    }

    const preference = await getNotificationPreference(currentUserId);
    if (!preference.preference || !preference.preference.enabled) {
      return { success: true, sent: false, reason: 'Notifications disabled for user' };
    }

    // Generate action URL
    const actionUrl = generateActionUrl(role, applicationId, disputeId);

    // Show browser notification
    const notification = showBrowserNotification({
      title,
      body: message,
      data: {
        applicationId,
        disputeId,
        actionUrl,
        role
      },
      tag: applicationId // Group notifications by application
    });

    return {
      success: true,
      sent: notification !== null,
      notification
    };
  } catch (error) {
    console.error('Error sending push notification to users:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send role-based push notification (mimics email notification structure)
 * @param {Object} params - Notification parameters
 * @param {string} params.type - Notification type (application, dispute, approval, etc.)
 * @param {string} params.title - Notification title
 * @param {string} params.message - Notification message
 * @param {string} params.applicationId - Application ID
 * @param {string} params.disputeId - Dispute ID (optional)
 * @param {Array<string>} params.roles - Roles to send to (e.g., ['admin', 'doctor', 'finance'])
 * @returns {Promise}
 */
export const sendRoleBasedPushNotification = async (params) => {
  try {
    const {
      type,
      title,
      message,
      applicationId,
      disputeId,
      roles = []
    } = params;

    if (!Array.isArray(roles) || roles.length === 0) {
      return { success: false, error: 'No roles specified' };
    }

    const results = [];
    
    // Send notification to each role
    for (const role of roles) {
      // Get users for this role
      const userIds = await getUsersByRole(role);
      
      if (userIds.length === 0) {
        console.log(`No users found for role: ${role}`);
        continue;
      }

      // Send to all users in this role
      const result = await sendPushNotificationToUsers({
        userIds,
        title,
        message,
        applicationId,
        disputeId,
        role
      });
      
      results.push({ role, ...result });
    }

    return {
      success: true,
      results
    };
  } catch (error) {
    console.error('Error sending role-based push notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send push notification when application is created
 * @param {Object} applicationData - Application data
 * @returns {Promise}
 */
export const sendApplicationCreatedPushNotification = async (applicationData) => {
  const patientName = applicationData.patient?.name || 'Patient';
  const appId = applicationData.applicationId;
  
  return await sendRoleBasedPushNotification({
    type: 'application',
    title: '📋 New Application Submitted',
    message: `New application for ${patientName} (${appId}) has been submitted.`,
    applicationId: appId,
    roles: ['admin', 'doctor', 'finance'] // Same as email recipients
  });
};

/**
 * Send push notification when query/dispute is raised
 * @param {string} applicationId - Application ID
 * @param {string} disputeId - Dispute ID
 * @param {string} raisedBy - Who raised the query
 * @param {string} category - Query category
 * @returns {Promise}
 */
export const sendQueryRaisedPushNotification = async (applicationId, disputeId, raisedBy, category) => {
  return await sendRoleBasedPushNotification({
    type: 'dispute',
    title: '⚠️ Query Raised',
    message: `A ${category} query has been raised on application ${applicationId} by ${raisedBy}.`,
    applicationId,
    disputeId,
    roles: ['admin', 'doctor', 'finance', 'hospital']
  });
};

/**
 * Send push notification when query is resolved
 * @param {string} applicationId - Application ID
 * @param {string} disputeId - Dispute ID
 * @param {string} resolvedBy - Who resolved the query
 * @returns {Promise}
 */
export const sendQueryResolvedPushNotification = async (applicationId, disputeId, resolvedBy) => {
  return await sendRoleBasedPushNotification({
    type: 'dispute',
    title: '✅ Query Resolved',
    message: `A query on application ${applicationId} has been resolved by ${resolvedBy}.`,
    applicationId,
    disputeId,
    roles: ['admin', 'doctor', 'finance']
  });
};

/**
 * Send push notification for doctor approval
 * @param {string} applicationId - Application ID
 * @param {string} patientName - Patient name
 * @returns {Promise}
 */
export const sendDoctorApprovalPushNotification = async (applicationId, patientName) => {
  return await sendRoleBasedPushNotification({
    type: 'approval',
    title: '✅ Doctor Approved',
    message: `Application for ${patientName} (${applicationId}) has been approved by doctor.`,
    applicationId,
    roles: ['finance'] // Finance team needs to know
  });
};

/**
 * Send push notification for financial approval
 * @param {string} applicationId - Application ID
 * @param {string} patientName - Patient name
 * @returns {Promise}
 */
export const sendFinancialApprovalPushNotification = async (applicationId, patientName) => {
  return await sendRoleBasedPushNotification({
    type: 'approval',
    title: '💰 Financial Approved',
    message: `Application for ${patientName} (${applicationId}) has been financially approved.`,
    applicationId,
    roles: ['doctor', 'admin'] // Doctor needs to know for final steps
  });
};

/**
 * Send push notification for policy approval
 * @param {string} applicationId - Application ID
 * @param {string} patientName - Patient name
 * @returns {Promise}
 */
export const sendPolicyApprovalPushNotification = async (applicationId, patientName) => {
  return await sendRoleBasedPushNotification({
    type: 'approval',
    title: '📋 Policy Verified',
    message: `Policy for ${patientName} (${applicationId}) has been verified.`,
    applicationId,
    roles: ['doctor', 'finance']
  });
};

/**
 * Get current user ID from auth context or local storage
 * Helper function - you may need to adjust based on your auth implementation
 * @returns {string|null}
 */
const getCurrentUserId = () => {
  // Try to get from localStorage (set by AuthContext)
  try {
    // Check multiple possible storage locations
    const authUser = localStorage.getItem('authUser');
    if (authUser) {
      const parsed = JSON.parse(authUser);
      return parsed.uid || parsed.id || null;
    }

    const user = localStorage.getItem('user');
    if (user) {
      const parsed = JSON.parse(user);
      return parsed.uid || parsed.id || null;
    }

    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsed = JSON.parse(userData);
      return parsed.uid || parsed.id || null;
    }
  } catch (e) {
    console.error('Error getting current user ID:', e);
  }
  return null;
};

export default {
  requestNotificationPermission,
  areNotificationsEnabled,
  saveNotificationPreference,
  getNotificationPreference,
  showBrowserNotification,
  sendPushNotificationToUsers,
  sendRoleBasedPushNotification,
  sendApplicationCreatedPushNotification,
  sendQueryRaisedPushNotification,
  sendQueryResolvedPushNotification,
  sendDoctorApprovalPushNotification,
  sendFinancialApprovalPushNotification,
  sendPolicyApprovalPushNotification
};
